import { z } from 'zod';
import { sectionHasValue } from '../util';
import { registry } from '../registry';
const yesNoUnableOptions = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
    { value: 'unable', label: 'Unable to Assess' },
];
const yesNoOptions = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
];
const betterSameWorseOptions = [
    { value: 'better', label: 'Better' },
    { value: 'same', label: 'Same' },
    { value: 'worse', label: 'Worse' },
];
const edemaScaleOptions = [
    { value: 'none', label: 'None' },
    { value: '+1', label: '+1' },
    { value: '+2', label: '+2' },
    { value: '+3', label: '+3' },
    { value: '+4', label: '+4' },
];
const pulseScaleOptions = [
    { value: '0', label: '0' },
    { value: '1+', label: '1+' },
    { value: '2+', label: '2+' },
    { value: '3+', label: '3+' },
    { value: '4+', label: '4+' },
];
const painSeverityOptions = Array.from({ length: 11 }, (_, i) => ({
    value: String(i),
    label: String(i),
}));
/** Section titles used by the Cardiovascular UI — single source for edit + read-only. */
export const cardiovascularSectionTitles = {
    chestPain: 'Chest Pain / Discomfort Since Last Visit',
    rightNow: 'Right Now During This Visit',
    chf: 'CHF / Volume Status',
    edema: 'Edema',
    peripheral: 'Peripheral Perfusion',
    ptPulse: 'Posterior Tibial (PT) Pulse',
    heartSounds: 'Heart Sounds',
    dvt: 'DVT Symptom Check',
    cardiacMeds: 'Cardiac Medication Compliance',
    education: 'Patient Education This Visit',
    cardiacStatus: 'Cardiac Status Determination',
};
export const baseCardiovascularSchema = z
    .object({
    wnl: z.boolean().optional().register(registry, { label: 'WNL' }),
    chestPainSinceLastVisit: z
        .string()
        .optional()
        .register(registry, {
        label: 'Chest pain or discomfort since last visit?',
        options: [...yesNoUnableOptions],
    }),
    chestPainWhen: z.string().optional().register(registry, { label: 'When did it occur?' }),
    chestPainProvocation: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Provocation — what brought it on or made it worse',
        options: [
            { value: 'exertion', label: 'Exertion' },
            { value: 'walking', label: 'Walking' },
            { value: 'climbing-stairs', label: 'Climbing stairs' },
            { value: 'emotional-stress', label: 'Emotional stress' },
            { value: 'lying-flat', label: 'Lying flat' },
            { value: 'deep-breathing', label: 'Deep breathing or coughing' },
            { value: 'eating', label: 'Eating or after meals' },
            { value: 'cold-air', label: 'Cold air or cold exposure' },
            { value: 'position-change', label: 'Movement or position change' },
            { value: 'chest-wall-pressure', label: 'Chest wall pressure or palpation' },
            { value: 'at-rest-no-trigger', label: 'At rest, no clear trigger' },
            { value: 'other-provocation', label: 'Other' },
        ],
    }),
    chestPainProvocationOther: z.string().optional().register(registry, { label: 'Other provocation (specify)' }),
    chestPainPalliation: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Palliation — what made it better or made it stop',
        options: [
            { value: 'rest', label: 'Rest' },
            { value: 'sitting-up', label: 'Sitting up or leaning forward' },
            { value: 'position-change', label: 'Position change' },
            { value: 'antacids', label: 'Antacids' },
            { value: 'deep-breathing', label: 'Deep breathing' },
            { value: 'nothing-helped', label: 'Nothing helped' },
            { value: 'resolved-on-own', label: 'Resolved on its own' },
            { value: 'other-palliation', label: 'Other' },
            { value: 'nitroglycerin', label: 'Nitroglycerin' },
        ],
    }),
    chestPainPalliationOther: z.string().optional().register(registry, { label: 'Other palliation (specify)' }),
    nitroglycerinDoses: z
        .string()
        .optional()
        .register(registry, {
        label: 'Doses',
        options: [
            { value: '1', label: '1' },
            { value: '2', label: '2' },
            { value: '3', label: '3' },
        ],
    }),
    nitroglycerinTimeToRelief: z.string().optional().register(registry, { label: 'Time to relief' }),
    chestPainQuality: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Quality',
        options: [
            { value: 'pressure', label: 'Pressure' },
            { value: 'squeezing', label: 'Squeezing' },
            { value: 'tightness', label: 'Tightness' },
            { value: 'heaviness', label: 'Heaviness' },
            { value: 'anginal', label: 'Anginal' },
            { value: 'sharp', label: 'Sharp' },
            { value: 'dull', label: 'Dull' },
            { value: 'burning', label: 'Burning' },
            { value: 'tearing', label: 'Tearing' },
        ],
    }),
    chestPainRadiation: z
        .string()
        .optional()
        .register(registry, {
        label: 'Radiation',
        options: [...yesNoOptions],
    }),
    chestPainRadiationDestination: z.string().optional().register(registry, { label: 'Radiation destination' }),
    chestPainSeverity: z.string().optional().register(registry, {
        label: 'Severity (0 = No pain · 10 = Worst pain)',
        options: painSeverityOptions,
    }),
    chestPainTiming: z
        .string()
        .optional()
        .register(registry, {
        label: 'Timing',
        options: [
            { value: 'constant', label: 'Constant' },
            { value: 'intermittent', label: 'Intermittent' },
            { value: 'episodic', label: 'Episodic' },
        ],
    }),
    chestPainDuration: z
        .string()
        .optional()
        .register(registry, {
        label: 'Duration',
        options: [
            { value: '>1min', label: '>1 min' },
            { value: '>3min', label: '>3 min' },
            { value: '>5min', label: '>5 min' },
            { value: '>10min', label: '>10 min' },
            { value: '>20min', label: '>20 min' },
            { value: '>30min', label: '>30 min' },
        ],
    }),
    calledAnyone: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Did they call anyone?',
        options: [
            { value: 'family-friend', label: 'Family / Friend' },
            { value: '911', label: '911' },
        ],
    }),
    associatedSymptoms: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Associated symptoms',
        options: [
            { value: 'sob', label: 'SOB' },
            { value: 'diaphoresis', label: 'Diaphoresis' },
            { value: 'palpitations', label: 'Palpitations' },
            { value: 'syncope', label: 'Syncope' },
            { value: 'dizziness', label: 'Dizziness' },
            { value: 'lightheadedness', label: 'Lightheadedness' },
            { value: 'nausea', label: 'Nausea' },
            { value: 'anxiety', label: 'Anxiety' },
        ],
    }),
    chestPainNewOrRecurrent: z
        .string()
        .optional()
        .register(registry, {
        label: 'New or recurrent?',
        options: [
            { value: 'new', label: 'New' },
            { value: 'recurrent', label: 'Recurrent' },
        ],
    }),
    chestPainNow: z
        .string()
        .optional()
        .register(registry, {
        label: 'Chest pain or pressure now?',
        options: [...yesNoOptions],
    }),
    chestPainNowSeverity: z.string().optional().register(registry, {
        label: 'Severity now (0 = No pain · 10 = Worst pain)',
        options: painSeverityOptions,
    }),
    sobAtRest: z
        .string()
        .optional()
        .register(registry, {
        label: 'New SOB at rest?',
        options: [...yesNoOptions],
    }),
    weightToday: z.string().optional().register(registry, { label: 'Weight today (lbs)' }),
    weightLastVisit: z.string().optional().register(registry, { label: 'Weight last visit (lbs)' }),
    edemaLle: z
        .string()
        .optional()
        .register(registry, {
        label: 'LLE',
        options: [...edemaScaleOptions],
    }),
    edemaRle: z
        .string()
        .optional()
        .register(registry, {
        label: 'RLE',
        options: [...edemaScaleOptions],
    }),
    edemaType: z
        .string()
        .optional()
        .register(registry, {
        label: 'Pitting or non-pitting',
        options: [
            { value: 'pitting', label: 'Pitting' },
            { value: 'non-pitting', label: 'Non-pitting' },
            { value: 'brawny', label: 'Brawny / indurated' },
        ],
    }),
    sacralEdema: z
        .string()
        .optional()
        .register(registry, {
        label: 'Sacral edema (bedbound patients)',
        options: [
            { value: 'present', label: 'Present' },
            { value: 'absent', label: 'Absent' },
        ],
    }),
    edemaWorsening: z
        .string()
        .optional()
        .register(registry, {
        label: 'Edema compared to last visit',
        options: [...betterSameWorseOptions],
    }),
    orthopneaPillows: z
        .string()
        .optional()
        .register(registry, {
        label: 'Orthopnea — pillows used at night',
        options: [
            { value: '0', label: '0' },
            { value: '1', label: '1' },
            { value: '2', label: '2' },
            { value: '3+', label: '3+' },
        ],
    }),
    orthopneaWorsening: z
        .string()
        .optional()
        .register(registry, {
        label: 'Orthopnea compared to last visit',
        options: [...betterSameWorseOptions],
    }),
    pndEpisodes: z.string().optional().register(registry, { label: 'PND episodes since last visit' }),
    dpLeft: z
        .string()
        .optional()
        .register(registry, {
        label: 'DP Left',
        options: [...pulseScaleOptions],
    }),
    dpRight: z
        .string()
        .optional()
        .register(registry, {
        label: 'DP Right',
        options: [...pulseScaleOptions],
    }),
    ptLeft: z
        .string()
        .optional()
        .register(registry, {
        label: 'PT Left',
        options: [...pulseScaleOptions],
    }),
    ptRight: z
        .string()
        .optional()
        .register(registry, {
        label: 'PT Right',
        options: [...pulseScaleOptions],
    }),
    capillaryLess: z
        .string()
        .optional()
        .register(registry, {
        label: 'Capillary Refill',
        options: [
            { value: 'less than 3 seconds', label: '< 3 seconds' },
            { value: 'more than 3 seconds', label: '> 3 seconds' },
            { value: 'unable', label: 'Unable' },
        ],
    }),
    heartSounds: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Heart Sounds',
        options: [
            { value: 'murmur-heard', label: 'Murmur heard' },
            { value: 'gallop-heard', label: 'Gallop heard' },
            { value: 'rub', label: 'Rub' },
            { value: 'other', label: 'Other' },
        ],
    }),
    heartSoundsDetail: z.string().optional().register(registry, { label: 'Murmur — grade, location, radiation' }),
    heartSoundsOther: z.string().optional().register(registry, { label: 'Other heart sounds (specify)' }),
    heartRate: z
        .string()
        .optional()
        .register(registry, {
        label: 'Heart Rate',
        options: [
            { value: 'normal', label: 'Normal (60–100 bpm)' },
            { value: 'bradycardia', label: 'Bradycardia < 60' },
            { value: 'tachycardia', label: 'Tachycardia > 100' },
        ],
    }),
    pacemakerIcd: z
        .string()
        .optional()
        .register(registry, {
        label: 'Pacemaker / ICD',
        options: [
            { value: 'none', label: 'None' },
            { value: 'pacemaker-normal', label: 'Pacemaker — functioning normally' },
            { value: 'pacemaker-uncertain', label: 'Pacemaker function uncertain' },
            { value: 'icd-no-shocks', label: 'ICD — no recent shocks' },
            { value: 'icd-shock', label: 'ICD shock reported' },
        ],
    }),
    dvtSymptoms: z
        .string()
        .optional()
        .register(registry, {
        label: 'New unilateral calf swelling, redness, warmth, or pain?',
        options: [...yesNoOptions],
    }),
    cardiacMedsTaken: z
        .string()
        .optional()
        .register(registry, {
        label: 'Cardiac meds taken as prescribed since last visit?',
        options: [
            { value: 'all', label: 'All' },
            { value: 'partial', label: 'Partial' },
            { value: 'none', label: 'None' },
        ],
    }),
    medPartialReason: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Reason',
        options: [
            { value: 'forgot', label: 'Forgot' },
            { value: 'cost', label: 'Cost' },
            { value: 'ran-out', label: 'Ran out' },
            { value: 'refused', label: 'Refused' },
            { value: 'pharmacy-delay', label: 'Pharmacy delay' },
            { value: 'other-reason', label: 'Other' },
            { value: 'side-effects-reported', label: 'Side effects reported' },
        ],
    }),
    medPartialReasonOther: z.string().optional().register(registry, { label: 'Other reason (specify)' }),
    medSideEffects: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Side effects',
        options: [
            { value: 'dizziness', label: 'Dizziness' },
            { value: 'dry-cough', label: 'Persistent dry cough' },
            { value: 'fatigue', label: 'Fatigue' },
            { value: 'edema-worse', label: 'Edema worse' },
            { value: 'unusual-bleeding', label: 'Unusual bleeding or bruising' },
            { value: 'muscle-pain', label: 'Muscle pain' },
            { value: 'gi-upset', label: 'GI upset' },
            { value: 'other-side-effect', label: 'Other' },
        ],
    }),
    medSideEffectsOther: z.string().optional().register(registry, { label: 'Other side effect (specify)' }),
    educationTopics: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Patient Education This Visit',
        options: [
            { value: 'low-sodium-diet', label: 'Low-sodium diet' },
            { value: 'daily-weight', label: 'Daily weight' },
            { value: 'med-compliance', label: 'Med compliance' },
            { value: 'symptom-recognition', label: 'Symptom recognition' },
            { value: 'when-to-call', label: 'When to call' },
            { value: 'activity-tolerance', label: 'Activity tolerance' },
            { value: 'smoking-cessation', label: 'Smoking cessation' },
            { value: 'other-education', label: 'Other' },
        ],
    }),
    educationTopicsOther: z.string().optional().register(registry, { label: 'Other topic (specify)' }),
    patientUnderstanding: z
        .string()
        .optional()
        .register(registry, {
        label: 'Patient / caregiver verbalized understanding?',
        options: [...yesNoOptions],
    }),
    callUnderstanding: z
        .string()
        .optional()
        .register(registry, {
        label: 'Patient / caregiver verbalized when to call us / 911?',
        options: [...yesNoOptions],
    }),
    educationFollowUpPlan: z.string().optional().register(registry, { label: 'Plan for next visit' }),
    cardiacStatus: z
        .string()
        .optional()
        .register(registry, {
        label: 'Cardiac status',
        options: [
            { value: 'stable', label: 'Stable — continues POC' },
            { value: 'worsening', label: 'Worsening — MD notified' },
            { value: 'acute', label: 'Acute change — 911/ED' },
        ],
    }),
    cardiovascularNarrative: z.string().optional().register(registry, { label: 'Cardiovascular Narrative' }),
})
    .register(registry, { label: 'Cardiovascular' });
export const baseCardiovascularSchemaRefined = baseCardiovascularSchema.refine((data) => sectionHasValue(data), {
    message: 'Mark as within normal limits if none applies',
    path: [],
});
